import { Component, OnInit, OnDestroy, OnChanges, AfterViewInit, SimpleChanges, Input, Output, EventEmitter, ChangeDetectorRef, HostListener, ChangeDetectionStrategy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { ModalService } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { TruthManagement, EvaluationCompletionEvent } from '../../services/truth-management';
import { DocumentFilesService } from '../../services/document-files.service'; 
import { ValidationRun, EvaluationStatus, ActiveEvaluation, EvaluationRunsResponse, EvaluationRunItem, DashboardEvalFile, DashboardResponse } from '../../models/truth-management.model';
import { Subscription, forkJoin ,Observable, Subject, of, EMPTY} from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { FileSelectionModalComponent } from '../file-selection-modal/file-selection-modal.component';
import { CanComponentDeactivate } from '../../guards/can-deactivate.guard';
import { PageHeaderComponent } from '../page-header/page-header.component';

@Component({
  selector: 'app-evaluation-runs',
  standalone: true,
  imports: [CommonModule, FormsModule, FileSelectionModalComponent, PageHeaderComponent],
  templateUrl: './evaluation-runs.html',
  styleUrl: './evaluation-runs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaluationRuns implements OnInit, OnDestroy, OnChanges, AfterViewInit, CanComponentDeactivate {
  @Input() documentModel: string = '';
  @Input() isEmbedded: boolean = false; // New flag to indicate if used as a tab
  @Output() evaluationSelected = new EventEmitter<string>(); // Emit evaluation ID when results button clicked
  @Output() evaluationStatusChanged = new EventEmitter<EvaluationStatus | null>(); // Emit when evaluation status changes
  /** Emitted once after loadBothRunTypes() resolves — lets parent skip a redundant eval/runs API call */
  @Output() latestRunsLoaded = new EventEmitter<{ evalRun: ValidationRun | null; testRun: ValidationRun | null }>();
  validationRuns: ValidationRun[] = []; // All runs (kept for backward compatibility)
  evaluationRuns: ValidationRun[] = []; // Full runs (files_run === total_files)
  testRuns: ValidationRun[] = []; // Partial runs (files_run !== total_files)
  latestEvalRun: ValidationRun | null = null;
latestTestRun: ValidationRun | null = null;
latestEvalRunStatus: EvaluationStatus | null = null;
latestTestRunStatus: EvaluationStatus | null = null;
  isAuthenticated = false;
  
// File Selection Modal (for partial validation runs - test runs only)
showFileSelectionModal = false;
modalFiles: DashboardEvalFile[] = []; //  Files for modal (from service)
isLoadingModalFiles = false; //  Loading state for modal
isTestRunMode = false; // Track if modal is opened for test run (forces partial mode)
  
  // Run type toggle (for displaying Evaluation Runs vs Test Runs)
  runType: 'evaluation' | 'test' = 'evaluation';
  
  // Active evaluation tracking - now subscribed from service
  activeEvaluations = new Map<string, ActiveEvaluation>();
  
  // PERFORMANCE: Cached properties to avoid excessive method calls in template
  currentActiveEvaluation: ActiveEvaluation | undefined = undefined;
  hasActiveEval: boolean = false;
  evaluationProgressPercent: number = 0;
  activeMatchedFieldsPercent: number = 0;
  activeUnmatchedFieldsPercent: number = 0;
  activeMatchedFieldsCount: number = 0;
  activeUnmatchedFieldsCount: number = 0;
  
  // BUG FIX 1: Cached boolean properties to prevent CSS flickering
  hasActiveFullEval: boolean = false;
  hasActiveTestEval: boolean = false;
  isStartingFullEval: boolean = false;
  isStartingTestEval: boolean = false;
  
  private pollingIntervals = new Map<string, any>();
  private elapsedTimeInterval: any = null;
  private readonly MAX_POLLING_ERRORS = 3; // Stop polling after 3 consecutive errors
  private pollingErrorCounts = new Map<string, number>(); // Track errors per evaluation
  private evaluationStateSubscription: Subscription | null = null; // Subscribe to service state
  private completionSubscription: Subscription | null = null; // Subscribe to completion events
  private recentlyCompletedRuns = new Set<string>(); // Track recently completed runs to prevent restart
  private isInitialLoad = true; // Track if this is the first load (to skip modals on page refresh)
  
  // Elapsed time: per-tab properties; elapsedTime getter returns the correct one
  evalElapsedTime: string = '';   // timer for the full evaluation run
  testElapsedTime: string = '';   // timer for the test run

  // Local backup timer: keeps counting during navigation/refresh until service timers$ takes over
  private localTimerInterval: any = null;

  // Active run status display properties (per run type)
  evalActiveRunStatus: { type: string; status: string; startTime: Date; duration: string } | null = null;
  testActiveRunStatus: { type: string; status: string; startTime: Date; duration: string } | null = null;

  // Cached per-run-type active evals
  currentActiveFullEval: ActiveEvaluation | undefined = undefined;
  currentActiveTestEval: ActiveEvaluation | undefined = undefined;
  
  // Loading state for API call
  loadingPastRuns = false;
  loadingSummary = true;
  showRunSummary = true;
  startingEvaluation = false; // Loading state when starting full evaluation
  startingPartialEvaluation = false; // Loading state when starting partial evaluation (selected files)
  
  // Attribute count from document_keys API
  documentAttributeCount: number = 0;
  
  // Flag to prevent duplicate loading
  private isLoading = false;
  private loadedDocumentModel: string = '';

  isEvalDetailsPopupOpen = false;
  activeEvalDetailsRun: ValidationRun | null = null;
  evalDetailsPopupMaxHeight = 'none';
  evalDetailsPopupPosition = { left: 0, top: 0 };
  @ViewChild('evalDetailsPopup') evalDetailsPopup?: ElementRef<HTMLElement>;

  private scrollListener = () => {
    if (this.isEvalDetailsPopupOpen) {
      this.closeEvalDetailsPopup();
      this.cdr.detectChanges();
    }
  };

  get latestRun(): ValidationRun | null {
  return this.runType === 'evaluation' ? this.latestEvalRun : this.latestTestRun;
}

get latestRunStatus(): EvaluationStatus | null {
  return this.runType === 'evaluation' ? this.latestEvalRunStatus : this.latestTestRunStatus;
}
  
  // ============================================
  // ACTIVE RUN STATUS DISPLAY (Phase 4)
  // ============================================
  
  // activeRunStatus is now a getter backed by evalActiveRunStatus / testActiveRunStatus

  /** Returns the elapsed time for the currently-selected tab's run. */
  get elapsedTime(): string {
    return this.runType === 'evaluation' ? this.evalElapsedTime : this.testElapsedTime;
  }

  /** Returns the active-run status badge data for the currently-selected tab. */
  get activeRunStatus(): { type: string; status: string; startTime: Date; duration: string } | null {
    return this.runType === 'evaluation' ? this.evalActiveRunStatus : this.testActiveRunStatus;
  }

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private truthManagementService: TruthManagement,
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private documentFilesService: DocumentFilesService,
    private notificationService: NotificationService,
    private clientCapability: ClientCapabilityService,
  ) {}

  /**
   * Initialize component state synchronously from service
   * CRITICAL: This prevents CSS flash on navigation by ensuring correct state before first render
   * Called in: ngOnInit(), ngOnChanges() when documentModel changes, route params subscription
   */
  private initializeStateSync(): void {
    // Get current state from service (synchronous - no waiting!)
    const currentActiveEvals = this.truthManagementService.getActiveEvaluations();
    this.activeEvaluations = new Map(currentActiveEvals);
    
    // Update all cached properties based on current state
    this.updateCurrentActiveEvaluation();
    
    // Sync starting flags independently per run type
    if (this.documentModel) {
      const fullKey = `${this.documentModel}:all`;
      const testKey = `${this.documentModel}:part`;
      const activeFullEval = currentActiveEvals.get(fullKey);
      const activeTestEval = currentActiveEvals.get(testKey);

      // Full eval flag
      if (activeFullEval) {
        const s = activeFullEval.status?.status;
        this.startingEvaluation = !s || s === 'PENDING';
      } else {
        this.startingEvaluation = false;
      }

      // Test run flag
      if (activeTestEval) {
        const s = activeTestEval.status?.status;
        this.startingPartialEvaluation = !s || s === 'PENDING';
      } else {
        this.startingPartialEvaluation = false;
      }

      // Auto-switch tab to match the active run type so the card is visible
      if (activeTestEval && !activeFullEval) {
        this.runType = 'test';
      } else if (activeFullEval && !activeTestEval) {
        this.runType = 'evaluation';
      }

      // Re-calculate cached props now that runType is correct
      this.updateCurrentActiveEvaluation();
    }
    
    // Compute elapsed time directly from started_at (no dependency on timers$ observable).
    // Read from the SERVICE (not local mirror) so this works even on first call before subscriptions run.
    if (this.documentModel) {
      const fKey = `${this.documentModel}:all`;
      const tKey = `${this.documentModel}:part`;
      const serviceEvals = this.truthManagementService.getActiveEvaluations();
      const aFull = serviceEvals.get(fKey);
      const aTest = serviceEvals.get(tKey);
      if (aFull?.started_at) {
        this.evalElapsedTime = this.computeElapsed(aFull.started_at);
      }
      if (aTest?.started_at) {
        this.testElapsedTime = this.computeElapsed(aTest.started_at);
      }

      // Start local backup timer for seamless counting until service timers$ takes over
      if (this.localTimerInterval) { clearInterval(this.localTimerInterval); this.localTimerInterval = null; }
      const fullStartedAt = aFull?.started_at;
      const testStartedAt = aTest?.started_at;
      if (fullStartedAt || testStartedAt) {
        this.localTimerInterval = setInterval(() => {
          if (fullStartedAt) this.evalElapsedTime = this.computeElapsed(fullStartedAt);
          if (testStartedAt) this.testElapsedTime = this.computeElapsed(testStartedAt);
          this.cdr.markForCheck();
        }, 1000);
      }
    }

    // Force immediate render so pie/progress/timer are visible on first frame
    this.cdr.detectChanges();
  }

  /** Compute formatted elapsed time from a started_at ISO string */
  private computeElapsed(startedAt: string): string {
    const startTime = new Date(startedAt.endsWith('Z') ? startedAt : startedAt + 'Z').getTime();
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    if (elapsed <= 0) return '0s';
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  ngOnInit(): void {
    this.isAuthenticated = this.authService.isAuthenticated;
    document.addEventListener('scroll', this.scrollListener, true);
    
    // Read documentModel synchronously from route snapshot (available before async params)
    // This ensures initializeStateSync() can match timer/progress keys on the very first call
    this.documentModel = this.documentModel || this.route.snapshot.params['documentModel'] || '';
    
    // Initialize state synchronously BEFORE subscription
    // This prevents button flickering when navigating back to the page
    this.initializeStateSync();
    
    this.evaluationStateSubscription = this.truthManagementService.activeEvaluations$
      .pipe(
        switchMap((activeEvals) => {
          // Update local state mirror (compound-keyed Map)
          this.activeEvaluations = new Map(activeEvals);

          // Guard: skip processing when documentModel isn't set yet (standalone route)
          if (!this.documentModel) return EMPTY;

          const fullKey = `${this.documentModel}:all`;
          const testKey = `${this.documentModel}:part`;
          const currentFullEval = activeEvals.get(fullKey);
          const currentTestEval = activeEvals.get(testKey);
          const hasActiveEval = !!(currentFullEval || currentTestEval);

          // Clear loading flags only when the RELEVANT run type disappears
          if (!currentFullEval) {
            this.startingEvaluation = false;
          }
          if (!currentTestEval) {
            this.startingPartialEvaluation = false;
          }
          if (!hasActiveEval && !this.localTimerInterval) {
            this.evalElapsedTime = '';
            this.testElapsedTime = '';
          }

          this.updateCurrentActiveEvaluation();
          this.cdr.markForCheck();

          // No active runs for this doc — return default pair
          if (!currentFullEval && !currentTestEval) {
            return of({ evalTimer: '', testTimer: '' });
          }

          // Subscribe to timers$ and return BOTH timers on each tick
          return this.truthManagementService.timers$.pipe(
            map(timers => ({
              evalTimer: currentFullEval ? (timers.get(currentFullEval.evaluation_id) || '') : '',
              testTimer: currentTestEval ? (timers.get(currentTestEval.evaluation_id) || '') : ''
            }))
          );
        })
      )
      .subscribe((timers: any) => {
        if (typeof timers === 'object' && timers !== null && 'evalTimer' in timers) {
          // Safety: never blank out elapsed time while an active eval exists
          if (timers.evalTimer || !this.hasActiveFullEval) {
            this.evalElapsedTime = timers.evalTimer;
          }
          if (timers.testTimer || !this.hasActiveTestEval) {
            this.testElapsedTime = timers.testTimer;
          }

          // Service timer is active — stop local backup timer (seamless handoff)
          if ((timers.evalTimer || timers.testTimer) && this.localTimerInterval) {
            clearInterval(this.localTimerInterval);
            this.localTimerInterval = null;
          }
        }
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });

    // Subscribe to completion events for popup, spinner clearing, and refresh
    this.completionSubscription = this.truthManagementService.evaluationCompleted$.subscribe(
      (event) => {
        // Skip popup on initial load (Phase 6: Initial Load Protection)
        if (this.isInitialLoad) {
          return;
        }
        this.handleEvaluationCompletion(event);
      }
    );
    
    // ── Active Run Status Display (per run type, tab-aware) ────────────────────
    this.truthManagementService.activeEvaluations$.subscribe(activeMap => {
      if (!this.documentModel) return;
      const fullKey = `${this.documentModel}:all`;
      const testKey = `${this.documentModel}:part`;
      const activeFullEval = activeMap.get(fullKey);
      const activeTestEval  = activeMap.get(testKey);

      const buildStatus = (eval_: ActiveEvaluation, type: string) => {
        if (!eval_ || eval_.status?.status === 'COMPLETED' || eval_.status?.status === 'FAILED') {
          return null;
        }
        const startTimeStr = eval_.started_at?.endsWith('Z') ? eval_.started_at : eval_.started_at + 'Z';
        return { type, status: eval_.status?.status || 'PENDING', startTime: new Date(startTimeStr), duration: '' };
      };

      this.evalActiveRunStatus = buildStatus(activeFullEval as ActiveEvaluation, 'Full Evaluation');
      this.testActiveRunStatus  = buildStatus(activeTestEval as ActiveEvaluation,  'Test Run');
      this.cdr.detectChanges();
    });

    // Subscribe to timers for live duration updates in status badge
    this.truthManagementService.timers$.subscribe(timersMap => {
      if (!this.documentModel) return;
      // Update full eval badge duration
      if (this.evalActiveRunStatus) {
        const activeFullEval = this.truthManagementService.getActiveFullEvaluation(this.documentModel);
        if (activeFullEval) {
          const timer = timersMap.get(activeFullEval.evaluation_id);
          if (timer) { this.evalActiveRunStatus.duration = timer; }
        }
      }
      // Update test run badge duration
      if (this.testActiveRunStatus) {
        const activeTestEval = this.truthManagementService.getActiveTestEvaluation(this.documentModel);
        if (activeTestEval) {
          const timer = timersMap.get(activeTestEval.evaluation_id);
          if (timer) { this.testActiveRunStatus.duration = timer; }
        }
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
    
    // Get document model from route params (for standalone page)
    this.route.params.subscribe(params => {
      if (params['documentModel']) {
        this.documentModel = params['documentModel'];
        // CRITICAL FIX 2: Re-initialize state with correct documentModel
        this.initializeStateSync();
        // Re-trigger all subscriptions now that documentModel is set
        this.truthManagementService.refreshActiveEvaluations();
        // Use setTimeout to defer until after all bindings complete
        setTimeout(() => this.loadDataIfNeeded(), 0);
      }
    });
  }

  /**
   * Handle input changes - important for embedded usage where documentModel comes from parent
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentModel']) {
      // CRITICAL FIX 3: Re-initialize state sync when documentModel changes
      // This handles embedded usage and prevents flash when switching documents
      if (!changes['documentModel'].firstChange) {
        this.initializeStateSync();
      }
      // Input changed - reload data if it's a new document model
      this.loadDataIfNeeded();
    }
  }

  /**
   * After view init - GUARANTEED data loading point after all @Input bindings are set
   */
  ngAfterViewInit(): void {
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    // This ensures we're outside the current change detection cycle
    setTimeout(() => {
      if (this.documentModel && !this.loadedDocumentModel) {
        this.loadDataIfNeeded();
      } else {
      }
    }, 0);
  }
  /**
   * Check if there are any active evaluations running
   */
  hasRunningEvaluations(): boolean {
    return this.activeEvaluations.size > 0 || 
           this.startingEvaluation || 
           this.startingPartialEvaluation;
  }

  /**
   * Handle evaluation completion event from service
   * Clears spinner and refreshes runs list
   * Note: Toaster notifications are handled centrally by TruthManagement service
   */
  private handleEvaluationCompletion(event: EvaluationCompletionEvent): void {
    // Only handle completions for this document model
    if (event.documentModel !== this.documentModel) {
      return;
    }
    
    // Skip on initial load (e.g., page refresh with completed run)
    if (this.isInitialLoad) {
      this.startingEvaluation = false;
      this.startingPartialEvaluation = false;
      this.cdr.detectChanges();
      return;
    }
    
    // Prevent duplicate handling
    if (this.recentlyCompletedRuns.has(event.evaluationId)) {
      return;
    }
    this.recentlyCompletedRuns.add(event.evaluationId);
    
    // Remove from recent set after 5 seconds
    setTimeout(() => {
      this.recentlyCompletedRuns.delete(event.evaluationId);
    }, 5000);
    
    // 1. Clear spinner flags
    this.startingEvaluation = false;
    this.startingPartialEvaluation = false;
    
    // 2. Show skeleton immediately (prevents stale data flash)
    this.loadingSummary = true;
    
    // 3. Targeted refresh: only fetch the completed run type's list.
    //    When test run completes while eval is still active, this avoids an unnecessary
    //    getEvaluationRuns('all') call (that list hasn't changed) and ensures the
    //    testRuns array + badge count ({{ testRuns.length }}) update immediately.
    this.loadRunsForType(event.runType);
    
    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Check if a full evaluation is starting for the current document
   * Resilient to component navigation - checks both local state and service state
   * @returns true if full evaluation is starting or in early stages (no status or PENDING)
   * CRITICAL FIX 4: Treats PENDING status as "starting" to prevent button flickering
   */
  isStartingEvaluation(): boolean {
    // Check local state first (fastest - set during API call)
    if (this.startingEvaluation) {
      return true;
    }
    // Fallback: Check service state (survives navigation) using compound key
    const activeFullEval = this.activeEvaluations.get(`${this.documentModel}:all`);
    if (activeFullEval) {
      const status = activeFullEval.status?.status;
      if (!status || status === 'PENDING') return true;
    }
    return false;
  }

  /**
   * Check if a test run is starting for the current document
   * Resilient to component navigation - checks both local state and service state
   * @returns true if test run is starting or in early stages (no status or PENDING)
   * CRITICAL FIX 5: Treats PENDING status as "starting" to prevent button flickering
   */
  isStartingTestRun(): boolean {
    // Check local state first (fastest - set during API call)
    if (this.startingPartialEvaluation) {
      return true;
    }
    // Fallback: Check service state (survives navigation) using compound key
    const activeTestEval = this.activeEvaluations.get(`${this.documentModel}:part`);
    if (activeTestEval) {
      const status = activeTestEval.status?.status;
      if (!status || status === 'PENDING') return true;
    }
    return false;
  }

  /**
   * Browser tab close/refresh prevention
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasRunningEvaluations()) {
      event.preventDefault();
      event.returnValue = '';
      return;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isEvalDetailsPopupOpen) { return; }
    const path = event.composedPath?.() ?? (event as any).path ?? [];
    const isInsidePopup = path.some((target: EventTarget) => {
      if (!(target instanceof HTMLElement)) { return false; }
      return target.classList.contains('status-inline-info') || target.classList.contains('status-details-popup');
    });
    if (!isInsidePopup) {
      this.closeEvalDetailsPopup();
      this.cdr.detectChanges();
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.isEvalDetailsPopupOpen) {
      this.closeEvalDetailsPopup();
      this.cdr.detectChanges();
    }
  }

  /**
   * Angular Router navigation prevention (CanDeactivate Guard)
   */
  canDeactivate(): Observable<boolean> {
    if (!this.hasRunningEvaluations()) {
      return of(true);
    }

    const navigationDecision$ = new Subject<boolean>();
    const activeCount = this.activeEvaluations.size;

    this.modalService.show({
      type: 'warning' as any,
      size: 'medium' as any,
      title: 'Evaluation in Progress',
      message: `You have ${activeCount} active evaluation(s) running for ${this.documentModel}.\n\nIf you leave this page, the evaluations will continue in the background, but you won't be able to see their progress.\n\nAre you sure you want to leave?`,
      confirmText: 'Yes, Leave',
      cancelText: 'Stay on Page',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onConfirm: () => {
       navigationDecision$.next(true);
        navigationDecision$.complete();
      },
      onCancel: () => {
        navigationDecision$.next(false);
        navigationDecision$.complete();
      }
    });

    return navigationDecision$.asObservable();
  }
  /**
   * Load data only if needed (prevent duplicate API calls)
   */
  private loadDataIfNeeded(): void {
    if (!this.documentModel) {
      return;
    }
    
    // Skip if already loading or already loaded for this document model
    if (this.isLoading || this.loadedDocumentModel === this.documentModel) {
      return;
    }
     this.loadedDocumentModel = this.documentModel;
    // Load both evaluation and test runs on initialization
    this.loadBothRunTypes();
    // NOTE: preloadFilesForModal() removed — files are loaded on-demand when user clicks "Run Test"
  }

  ngOnDestroy(): void {
    // Clean up service subscription
    if (this.evaluationStateSubscription) {
      this.evaluationStateSubscription.unsubscribe();
    }
    
    // Clean up completion subscription
    if (this.completionSubscription) {
      this.completionSubscription.unsubscribe();
    }
    
    // Clean up all polling intervals
    this.pollingIntervals.forEach(interval => clearInterval(interval));
    this.pollingIntervals.clear();

    // Clean up local backup timer
    if (this.localTimerInterval) { clearInterval(this.localTimerInterval); this.localTimerInterval = null; }

    document.removeEventListener('scroll', this.scrollListener, true);
  }

  /**
   * Refresh only the runs for the specified run type after it completes.
   * Leaves the other run type's arrays untouched so a concurrent active run is not disrupted.
   * Updates testRuns/evaluationRuns arrays and the tab-count badge ({{ testRuns.length }}).
   */
  private loadRunsForType(runType: 'all' | 'part'): void {
    if (!this.documentModel) return;

    this.truthManagementService.getEvaluationRuns(this.documentModel, runType).subscribe({
      next: (result: EvaluationRunsResponse) => {
        // Cache so subsequent navigations show fresh data immediately
        this.truthManagementService.cacheEvaluationRuns(this.documentModel, runType, result);
        // Pass undefined for the other type — processBothRunTypesResult leaves those arrays untouched
        if (runType === 'all') {
          this.processBothRunTypesResult(result, undefined);
        } else {
          this.processBothRunTypesResult(undefined, result);
        }
        this.isLoading = false;
        this.loadingPastRuns = false;
        if (this.isInitialLoad) { this.isInitialLoad = false; }
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isLoading = false;
        this.loadingPastRuns = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Load both evaluation runs (run_type=all) and test runs (run_type=part) in parallel
   * This ensures both tabs have data when component loads
   */
  loadBothRunTypes(): void {
    if (!this.documentModel) {
      return;
    }
    
    // ── Stale-While-Revalidate: show cached data instantly on back-navigation ──
    const cachedEval = this.truthManagementService.getCachedEvaluationRuns(this.documentModel, 'all');
    const cachedTest = this.truthManagementService.getCachedEvaluationRuns(this.documentModel, 'part');
    const hasCachedData = !!(cachedEval || cachedTest);

    if (hasCachedData) {
      // Process cached data immediately (no loading spinners)
      this.processBothRunTypesResult(cachedEval, cachedTest);
      this.isLoading = false;
      this.loadingPastRuns = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } else {
      // No cache — show loading state as before
      this.isLoading = true;
      this.loadingPastRuns = true;
      this.cdr.detectChanges();
    }

    // Background refresh policy: skip network call when all cached runs are COMPLETED/FAILED.
    // A background call is only worthwhile when:
    //   a) the cache is cold (first load), or
    //   b) a RUNNING/PENDING run exists whose state may have changed since last fetch.
    // This eliminates redundant API calls on every back-navigation for finished evaluations.
    const hasActiveRunsInCache =
      cachedEval?.runs?.some(r => r.status === 'RUNNING' || r.status === 'PENDING') ||
      cachedTest?.runs?.some(r => r.status === 'RUNNING' || r.status === 'PENDING');

    if (hasCachedData && !hasActiveRunsInCache) {
      // Cache is fresh and contains only terminal-state runs — no refresh needed.
      // Mark initial load done so completion events are not skipped after this point.
      this.isInitialLoad = false;
      // Still fetch the attribute count badge (lightweight POST, not a runs list call).
      this.fetchDocumentAttributeCount();
      return;
    }

    // Cache miss or has active runs — make fresh API calls
    forkJoin({
      evaluationRuns: this.truthManagementService.getEvaluationRuns(this.documentModel, 'all'),
      testRuns: this.truthManagementService.getEvaluationRuns(this.documentModel, 'part')
    }).subscribe({
      next: (result) => {
        // Cache fresh responses for next navigation
        if (result.evaluationRuns) {
          this.truthManagementService.cacheEvaluationRuns(this.documentModel, 'all', result.evaluationRuns);
        }
        if (result.testRuns) {
          this.truthManagementService.cacheEvaluationRuns(this.documentModel, 'part', result.testRuns);
        }

        // Process fresh data
        this.processBothRunTypesResult(result.evaluationRuns, result.testRuns);
        
        this.isLoading = false;
        this.loadingPastRuns = false;
        
        // Mark initial load as complete after first successful load
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
        }
        // Fetch attribute count from document_keys API
        this.fetchDocumentAttributeCount();
        
        // CRITICAL: Force change detection to ensure UI updates
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: (error) => {
        // Ensure arrays are properly initialized even on error (only if no cache was shown)
        if (!hasCachedData) {
          this.evaluationRuns = [];
          this.testRuns = [];
          this.validationRuns = [];
        }
        this.isLoading = false;
        this.loadingPastRuns = false;
        
        // Force change detection on error path too
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Process evaluation runs + test runs response data (shared by cache path and API path)
   */
  private processBothRunTypesResult(
    evalResponse: EvaluationRunsResponse | undefined,
    testResponse: EvaluationRunsResponse | undefined
  ): void {
    // Process evaluation runs (run_type=all) - full validation runs
    if (evalResponse?.status === 'SUCCESS' && evalResponse.runs) {
      const completedEvalRuns = evalResponse.runs
        .filter((run: EvaluationRunItem) => run.status !== 'PENDING' && run.status !== 'RUNNING')
        .map((run: EvaluationRunItem) => this.mapApiRunToValidationRun(run));
      
      this.evaluationRuns = completedEvalRuns;
      
      this.evaluationRuns.sort((a, b) => {
        const dateA = new Date(a.createdDate).getTime();
        const dateB = new Date(b.createdDate).getTime();
        return dateB - dateA;
      });
      // Check for active evaluations
      const activeEvalRuns = evalResponse.runs.filter(
        (run: EvaluationRunItem) => run.status === 'PENDING' || run.status === 'RUNNING'
      );
      activeEvalRuns.forEach((run: EvaluationRunItem) => {
        if (this.pollingIntervals.has(run.evaluation_id)) return;
        if (this.recentlyCompletedRuns.has(run.evaluation_id)) return;

        const existingFullEval = this.activeEvaluations.get(`${this.documentModel}:all`);
        if (existingFullEval && existingFullEval.evaluation_id === run.evaluation_id) {
          this.truthManagementService.startStatusPolling(run.evaluation_id, this.documentModel);
          return;
        }
        
        const activeEval: ActiveEvaluation = {
          evaluation_id: run.evaluation_id,
          job_id: run.evaluation_id,
          document_model: this.documentModel,
          file_count: run.total_files || 0,
          started_at: run.started_at,
          status: null,
          run_type: 'all'
        };
        this.activeEvaluations.set(`${this.documentModel}:all`, activeEval);
        this.truthManagementService.setActiveEvaluation(this.documentModel, activeEval);
        this.updateCurrentActiveEvaluation();
        this.truthManagementService.startStatusPolling(run.evaluation_id, this.documentModel);
      });
    } else if (evalResponse) {
      this.evaluationRuns = [];
    }

    // Process test runs (run_type=part) - partial validation runs
    if (testResponse?.status === 'SUCCESS' && testResponse.runs) {
      const completedTestRuns = testResponse.runs
        .filter((run: EvaluationRunItem) => run.status !== 'PENDING' && run.status !== 'RUNNING')
        .map((run: EvaluationRunItem) => this.mapApiRunToValidationRun(run));
      
      this.testRuns = completedTestRuns;
      
      this.testRuns.sort((a, b) => {
        const dateA = new Date(a.createdDate).getTime();
        const dateB = new Date(b.createdDate).getTime();
        return dateB - dateA;
      });
     
      // Check for active test runs
      const activeTestRuns = testResponse.runs.filter(
        (run: EvaluationRunItem) => run.status === 'PENDING' || run.status === 'RUNNING'
      );
      activeTestRuns.forEach((run: EvaluationRunItem) => {
        if (this.recentlyCompletedRuns.has(run.evaluation_id)) return;
        if (this.pollingIntervals.has(run.evaluation_id)) return;

        const existingTestEval = this.activeEvaluations.get(`${this.documentModel}:part`);
        if (existingTestEval && existingTestEval.evaluation_id === run.evaluation_id) {
          this.truthManagementService.startStatusPolling(run.evaluation_id, this.documentModel);
          return;
        }
        
        const activeEval: ActiveEvaluation = {
          evaluation_id: run.evaluation_id,
          job_id: run.evaluation_id,
          document_model: this.documentModel,
          file_count: run.total_files || 0,
          started_at: run.started_at,
          status: null,
          run_type: 'part'
        };
        this.activeEvaluations.set(`${this.documentModel}:part`, activeEval);
        this.truthManagementService.setActiveEvaluation(this.documentModel, activeEval);
        this.updateCurrentActiveEvaluation();
        this.truthManagementService.startStatusPolling(run.evaluation_id, this.documentModel);
      });
    } else if (testResponse) {
      this.testRuns = [];
    }

    // Set latest run for summary display (tab-aware)
    if (this.evaluationRuns.length > 0) {
      this.latestEvalRun = this.evaluationRuns[0];
    }
    if (this.testRuns.length > 0) {
      this.latestTestRun = this.testRuns[0];
    }

    // Combine both for validationRuns (backward compatibility)
    this.validationRuns = [...this.evaluationRuns, ...this.testRuns];
    this.validationRuns.sort((a, b) => {
      const dateA = (a.completedDate || a.createdDate).getTime();
      const dateB = (b.completedDate || b.createdDate).getTime();
      return dateB - dateA;
    });
    
    // Set editableEvaluationId based on active tab
    if (this.runType === 'evaluation' && this.latestEvalRun) {
      this.editableEvaluationId = this.latestEvalRun.evaluationId || '';
    } else if (this.runType === 'test' && this.latestTestRun) {
      this.editableEvaluationId = this.latestTestRun.evaluationId || '';
    }

    // Notify parent with loaded runs data — parent uses this to avoid a duplicate eval/runs API call
    this.latestRunsLoaded.emit({ evalRun: this.latestEvalRun, testRun: this.latestTestRun });

    // Fetch status for the active tab's latest run
    this.fetchLatestRunStatus();
  }

  /**
   * Load all run data in a single API call (combines past runs and active evaluations)
   */
  loadAllRunData(): void {
    if (!this.documentModel) {
      return;
    }
    
    this.isLoading = true;
    this.loadingPastRuns = true;
    
    // Map component runType to API run_type parameter
    // 'evaluation' -> 'all' (full runs only)
    // 'test' -> 'part' (partial/test runs only)
    const apiRunType: 'all' | 'part' | undefined = this.runType === 'evaluation' ? 'all' : this.runType === 'test' ? 'part' : undefined;
    
    // Single API call to fetch evaluation runs filtered by run_type
    this.truthManagementService.getEvaluationRuns(this.documentModel, apiRunType).subscribe({
      next: (response: EvaluationRunsResponse) => {
        if (response.status === 'SUCCESS' && response.runs) {
          // Separate completed runs from active runs
          const completedRuns: EvaluationRunItem[] = [];
          const activeRuns: EvaluationRunItem[] = [];
          
          response.runs.forEach((run: EvaluationRunItem) => {
            if (run.status === 'PENDING' || run.status === 'RUNNING') {
              activeRuns.push(run);
            } else {
              completedRuns.push(run);
            }
          });
          
          // Process completed runs as past runs
          const allCompletedRuns = completedRuns.map((run: EvaluationRunItem) => this.mapApiRunToValidationRun(run));
          
          // Backend now filters by run_type, so we can directly assign to the appropriate array
          // But for backward compatibility and to handle case where API returns all runs,
          // we still filter based on files_run
          if (this.runType === 'evaluation') {
            // Should contain only full runs (files_run === total_files)
            this.evaluationRuns = allCompletedRuns.filter((run: ValidationRun) => {
              const filesRun = (run as any).filesRun || run.totalFiles;
              return filesRun === run.totalFiles;
            });
          } else if (this.runType === 'test') {
            // Should contain only partial runs (files_run !== total_files)
            this.testRuns = allCompletedRuns.filter((run: ValidationRun) => {
              const filesRun = (run as any).filesRun || run.totalFiles;
              return filesRun !== run.totalFiles;
            });
          } else {
            // Fallback: if no specific type, filter into both arrays
            this.evaluationRuns = [];
            this.testRuns = [];
            allCompletedRuns.forEach((run: ValidationRun) => {
              const filesRun = (run as any).filesRun || run.totalFiles;
              if (filesRun === run.totalFiles) {
                this.evaluationRuns.push(run);
              } else {
                this.testRuns.push(run);
              }
            });
            }
          
          // Keep validationRuns for backward compatibility (combine both)
          this.validationRuns = allCompletedRuns;
          
          // Sort by date descending (most recent first)
          this.evaluationRuns.sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA;
          });
          this.testRuns.sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA;
          });
          this.validationRuns.sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA;
          }); 
          // Set latest run for summary display (tab-aware)
          if (this.evaluationRuns.length > 0) {
            this.latestEvalRun = this.evaluationRuns[0];
          }
          if (this.testRuns.length > 0) {
            this.latestTestRun = this.testRuns[0];
          }
          // Set editableEvaluationId based on active tab
          if (this.runType === 'evaluation' && this.latestEvalRun) {
            this.editableEvaluationId = this.latestEvalRun.evaluationId || '';
          } else if (this.runType === 'test' && this.latestTestRun) {
            this.editableEvaluationId = this.latestTestRun.evaluationId || '';
          }
          // Fetch the status for both run types
          this.fetchLatestRunStatus();
          
          // Process active evaluations
          activeRuns.forEach((run: EvaluationRunItem) => {
            const activeEval: ActiveEvaluation = {
              evaluation_id: run.evaluation_id,
              job_id: run.evaluation_id,
              document_model: this.documentModel,
              file_count: run.total_files || 0,
              started_at: run.started_at,
              status: null
            };
            
            this.activeEvaluations.set(this.documentModel, activeEval);
            this.updateCurrentActiveEvaluation();
            
            // Start polling for this evaluation
            this.truthManagementService.startStatusPolling(run.evaluation_id, this.documentModel);
          });
          
        } else {
          this.validationRuns = [];
        }
        
        this.isLoading = false;
        this.loadingPastRuns = false;
        
        // Fetch attribute count from document_keys API
        this.fetchDocumentAttributeCount();
        
        // Trigger UI update
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.validationRuns = [];
        this.isLoading = false;
        this.loadingPastRuns = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadPastRuns(): void {    
    if (!this.documentModel) {
      return;
    }
    
    this.isLoading = true;
    this.loadingPastRuns = true;
    
    // Fetch evaluation runs from API
    this.truthManagementService.getEvaluationRuns(this.documentModel).subscribe({
      next: (response: EvaluationRunsResponse) => {
        if (response.status === 'SUCCESS' && response.runs) {
          // Map API runs to ValidationRun format
          this.validationRuns = response.runs.map((run: EvaluationRunItem) => this.mapApiRunToValidationRun(run));
          
          // Sort by date descending (most recent first)
          this.validationRuns.sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA;
          });
         // Set latest run for summary display (tab-aware)
          if (this.evaluationRuns.length > 0) {
            this.latestEvalRun = this.evaluationRuns[0];
          }
          if (this.testRuns.length > 0) {
            this.latestTestRun = this.testRuns[0];
          }
          // Set editableEvaluationId based on active tab
          if (this.runType === 'evaluation' && this.latestEvalRun) {
            this.editableEvaluationId = this.latestEvalRun.evaluationId || '';
          } else if (this.runType === 'test' && this.latestTestRun) {
            this.editableEvaluationId = this.latestTestRun.evaluationId || '';
          }
          // Fetch the status for both run types
          this.fetchLatestRunStatus();
        } else {
          this.validationRuns = [];
        }
        
        this.isLoading = false;
        this.loadingPastRuns = false;
        
        // Fetch attribute count from document_keys API
        this.fetchDocumentAttributeCount();
        
        // Trigger UI update
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.validationRuns = [];
        this.isLoading = false;
        this.loadingPastRuns = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Map API run data to ValidationRun format (kept for potential future use)
   */
  mapApiRunToValidationRun(run: any): ValidationRun {
    // Parse accuracy - handle both number and string formats
    // API returns 0-1 scale, multiply by 100 for percentage display
    let accuracy = 0;
    if (typeof run.overall_accuracy === 'number') {
      accuracy = run.overall_accuracy * 100;
    } else if (typeof run.overall_accuracy === 'string') {
      const parsed = parseFloat(run.overall_accuracy.replace('%', '')) || 0;
      // If value is less than 1, it's in 0-1 scale, multiply by 100
      accuracy = parsed <= 1 ? parsed * 100 : parsed;
    }
    
    // Calculate duration - prefer time_taken_seconds, fallback to computing from dates
    let totalSeconds = Math.floor(run.time_taken_seconds || 0);
     // If time_taken_seconds is 0 or missing, try to calculate from started_at and completed_at
    if (totalSeconds === 0 && run.started_at) {
      const startDate = new Date(run.started_at.endsWith('Z') ? run.started_at : run.started_at + 'Z');
      
      if (run.completed_at) {
        // Completed run - calculate from completed_at
        const endDate = new Date(run.completed_at.endsWith('Z') ? run.completed_at : run.completed_at + 'Z');
        totalSeconds = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);
     } else {
        // No completed_at - calculate from start to now for any completed/failed status
        const statusUpper = (run.status || '').toUpperCase();
        if (statusUpper === 'COMPLETED' || statusUpper === 'FAILED') {
          totalSeconds = Math.floor((Date.now() - startDate.getTime()) / 1000);
         }
      }
    }
    
    // If still no duration and we have started_at, calculate from now as last resort
    if (totalSeconds === 0 && run.started_at) {
      const startDate = new Date(run.started_at.endsWith('Z') ? run.started_at : run.started_at + 'Z');
      totalSeconds = Math.floor((Date.now() - startDate.getTime()) / 1000);
    }
    
    // Format duration
    let duration = '';
    if (totalSeconds > 0) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (hours > 0) {
        duration = `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        duration = `${minutes}m ${seconds}s`;
      } else {
        duration = `${seconds}s`;
      }
    }
   
    // Parse date - ensure UTC handling if no timezone specified
    let createdDate: Date;
    if (run.started_at) {
      // If the date string doesn't have timezone info, treat it as UTC
      const dateStr = run.started_at.endsWith('Z') ? run.started_at : run.started_at + 'Z';
      createdDate = new Date(dateStr);
    } else {
      createdDate = new Date();
    }
    
    // Parse completed date for proper sorting (most recently completed should be latest)
    let completedDate: Date | undefined;
    if (run.completed_at) {
      const completedDateStr = run.completed_at.endsWith('Z') ? run.completed_at : run.completed_at + 'Z';
      completedDate = new Date(completedDateStr);
    }
    
    return {
      runId: run.evaluation_id,
      evaluationId: run.evaluation_id,
      loanId: 0,
      loanNumber: this.documentModel,
      documentCode: this.documentModel,
      accuracy: accuracy,
      status: run.status === 'COMPLETED' ? 'Completed' : run.status === 'FAILED' ? 'Failed' : run.status,
      createdDate: createdDate,
      completedDate: completedDate,
      totalFiles: run.total_files,
      completedFiles: run.completed_files,
      failedFiles: run.failed_files,
      duration: duration,
      durationMs: totalSeconds * 1000,
      attributeCount: this.documentAttributeCount, // Will be updated by fetchDocumentAttributeCount
      filesRun: run.files_run || run.total_files, // Add files_run for classification (default to total_files if missing)
      ocrMode: run.ocr_mode || undefined,
      llmModel: run.llm_model || undefined,
    };
  }

  /**
   * Fetch the correct attribute count from document_keys API
   */
  fetchDocumentAttributeCount(): void {
    if (!this.documentModel) return;
    this.truthManagementService.getDocumentKeys(this.documentModel).subscribe({
      next: (response: any) => {
        const keys = response.keys || response || [];
        this.documentAttributeCount = Array.isArray(keys) ? keys.length : 0; 
        // Update all validation runs with the correct attribute count
        this.validationRuns.forEach(run => {
          run.attributeCount = this.documentAttributeCount;
        });
        
        // Force UI update
        this.validationRuns = [...this.validationRuns];
        this.cdr.detectChanges();
      },
      error: (error) => {
      }
    });
  }

  /**
   * Get accuracy from status API, fallback to latestRun.accuracy
   * This ensures consistent accuracy display across the app
   */
  getLatestAccuracy(): number {
    // Use status API's summary.overall_accuracy (0-1 scale, multiply by 100)
    if (this.latestRunStatus?.summary?.overall_accuracy !== undefined) {
      return this.latestRunStatus.summary.overall_accuracy * 100;
    }
    // Fallback: Use the runs API accuracy
    return this.latestRun?.accuracy ?? 0;
  }

  getAvgElapsedTime(): string {
    const runs = (this.runType === 'evaluation' ? this.evaluationRuns : this.testRuns)
      .filter(r => (r.status === 'COMPLETED' || r.status === 'Completed') && r.durationMs != null && r.durationMs > 0);

    if (runs.length === 0) return '';

    // Take 10 most recent by date
    const recent = [...runs]
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
      .slice(0, 10);

    // Sort by duration ascending to locate min/max
    const sorted = [...recent].sort((a, b) => (a.durationMs ?? 0) - (b.durationMs ?? 0));

    // Trim min+max only when 4+ runs; otherwise plain average of all
    const toAverage = sorted.length >= 4 ? sorted.slice(1, sorted.length - 1) : sorted;

    const avgMs = toAverage.reduce((sum, r) => sum + (r.durationMs ?? 0), 0) / toAverage.length;
    const totalSec = Math.round(avgMs / 1000);
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  }

  truncateDocumentModelName(name: string | null | undefined, maxLength: number = 24): string {
    if (!name) {
      return '-';
    }
    return name.length > maxLength ? `${name.slice(0, maxLength - 1)}…` : name;
  }

  private getMatchedFields(status: EvaluationStatus | null | undefined): number {
    return status?.summary?.total_matched_fields ?? 0;
  }

  private getTotalFields(status: EvaluationStatus | null | undefined): number {
    return status?.summary?.total_fields ?? 0;
  }

  private getUnmatchedFields(status: EvaluationStatus | null | undefined): number {
    const total = this.getTotalFields(status);
    const matched = this.getMatchedFields(status);
    return Math.max(total - matched, 0);
  }

  private getFieldPercent(value: number, total: number): number {
    if (!total) {
      return 0;
    }
    return Math.round((value / total) * 100);
  }

  getLatestMatchedFieldsCount(): number {
    return this.getMatchedFields(this.latestRunStatus);
  }

  getLatestUnmatchedFieldsCount(): number {
    return this.getUnmatchedFields(this.latestRunStatus);
  }

  getLatestMatchedFieldsPercent(): number {
    return this.getFieldPercent(this.getLatestMatchedFieldsCount(), this.getTotalFields(this.latestRunStatus));
  }

  getLatestUnmatchedFieldsPercent(): number {
    return this.getFieldPercent(this.getLatestUnmatchedFieldsCount(), this.getTotalFields(this.latestRunStatus));
  }

  getActiveMatchedFieldsCount(): number {
    return this.getMatchedFields(this.currentActiveEvaluation?.status);
  }

  getActiveUnmatchedFieldsCount(): number {
    return this.getUnmatchedFields(this.currentActiveEvaluation?.status);
  }

  getActiveMatchedFieldsPercent(): number {
    return this.getFieldPercent(this.getActiveMatchedFieldsCount(), this.getTotalFields(this.currentActiveEvaluation?.status));
  }

  getActiveUnmatchedFieldsPercent(): number {
    return this.getFieldPercent(this.getActiveUnmatchedFieldsCount(), this.getTotalFields(this.currentActiveEvaluation?.status));
  }

  /**
   * Fetch the evaluation status for the latest run to display in summary
   * Tab-aware: fetches status for both eval and test runs
   */
  fetchLatestRunStatus(): void {
    this.loadingSummary = true;
    
    // Only fetch the active tab's run — defer the other tab until the user switches to it
    const activeRun = this.runType === 'test' ? this.latestTestRun : this.latestEvalRun;
    
    if (activeRun?.evaluationId) {
      this.truthManagementService.getEvaluationStatus(activeRun.evaluationId).subscribe({
        next: (status: EvaluationStatus) => {
          if (this.runType === 'test') {
            this.latestTestRunStatus = status;
            if (this.latestTestRun) {
              this.latestTestRun.status = status.status || this.latestTestRun.status;
            }
          } else {
            this.latestEvalRunStatus = status;
            if (this.latestEvalRun) {
              this.latestEvalRun.status = status.status || this.latestEvalRun.status;
            }
            // Emit to parent so it can update its evaluationStatus (eval tab only)
            this.evaluationStatusChanged.emit(status);
          }
          // Push into service-level status store so any component can read it without re-fetching
          if (activeRun.evaluationId) {
            this.truthManagementService.updateLatestEvalStatus(activeRun.evaluationId, status);
          }
          this.loadingSummary = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          // Set a minimal fallback status so the UI is not stuck
          const fallbackStatus: EvaluationStatus = {
            evaluation_id: activeRun.evaluationId || '',
            status: 'COMPLETED',
            total_files: activeRun.totalFiles || 0,
            completed_files: activeRun.completedFiles || 0,
            running_files: 0,
            pending_files: 0,
            failed_files: activeRun.failedFiles || 0,
            started_at: activeRun.createdDate?.toISOString() || '',
            completed_at: null,
            files: [],
            results: []
          };
          if (this.runType === 'test') {
            this.latestTestRunStatus = fallbackStatus;
          } else {
            this.latestEvalRunStatus = fallbackStatus;
          }
          this.loadingSummary = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      this.loadingSummary = false;
    }
  }

  // Editable evaluation ID for input field
  editableEvaluationId: string = '';
  // Timestamp captured when the user clicks "Run New Validation" (used if confirmation delays start)
  pendingStartTime: string | null = null;

  /**
   * Handle evaluation ID input changes
   */
  onEvaluationIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.editableEvaluationId = input.value;
  }

  /**
   * Load evaluation data from the input field value
   */
  loadEvaluationFromInput(): void {
    const newId = this.editableEvaluationId.trim();
    // Only fetch if the ID is different and not empty
    if (newId && newId !== this.latestRun?.evaluationId) {
      this.fetchEvaluationById(newId);
    } else {
    }
  }

  /**
   * Handle evaluation ID change from user input (edit or paste) - legacy method
   */
  onEvaluationIdChange(event: Event): void {
    const target = event.target as HTMLElement;
    const newId = target.textContent?.trim() || '';
    // Only fetch if the ID is different and not empty
    if (newId && newId !== this.latestRun?.evaluationId) {
      this.fetchEvaluationById(newId);
    }
  }

  /**
   * Fetch evaluation data by a specific evaluation ID
   */
  fetchEvaluationById(evaluationId: string): void {
    this.loadingSummary = true;
    this.cdr.detectChanges();
    
    this.truthManagementService.getEvaluationStatus(evaluationId).subscribe({
      next: (status: EvaluationStatus) => { 
        // Create the run object
        const fetchedRun: ValidationRun = {
          runId: evaluationId,
          evaluationId: evaluationId,
          documentCode: this.documentModel,
          loanId: this.latestRun?.loanId || 0,
          loanNumber: this.latestRun?.loanNumber || '',
          createdDate: status.started_at ? new Date(status.started_at) : new Date(),
          duration: this.calculateDurationFromStatus(status.started_at, status.completed_at),
          status: status.status || 'COMPLETED',
          totalFiles: status.total_files || 0,
          completedFiles: status.completed_files || 0,
          failedFiles: status.failed_files || 0,
          accuracy: status.summary?.overall_accuracy ? status.summary.overall_accuracy * 100 : 0
        };
        
        // Determine which backing property to update based on which list contains this ID
        // Check if it's in evaluationRuns or testRuns
        const isEvalRun = this.evaluationRuns.some(r => r.evaluationId === evaluationId);
        const isTestRun = this.testRuns.some(r => r.evaluationId === evaluationId);
        
        if (isEvalRun) {
          this.latestEvalRun = fetchedRun;
          this.latestEvalRunStatus = status;
        } else if (isTestRun) {
          this.latestTestRun = fetchedRun;
          this.latestTestRunStatus = status;
        } else {
          // If not in either list, default to eval
          this.latestEvalRun = fetchedRun;
          this.latestEvalRunStatus = status;
        }
        
        // Update the editable field to reflect the new ID
        this.editableEvaluationId = evaluationId;
        
        // Emit the status change so parent components can react
        this.evaluationStatusChanged.emit(status);
        
        this.loadingSummary = false;
        this.cdr.detectChanges();
        
      },
      error: (error) => {
        this.loadingSummary = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Calculate duration between start and end times
   */
  private calculateDurationFromStatus(startAt: string | null, completedAt: string | null): string {
    if (!startAt) return '-';
    
    const start = new Date(startAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Load active (PENDING/RUNNING) evaluations from API
   */
  loadActiveEvaluationsFromAPI(): void { 
    if (!this.documentModel) {
     return;
    }
    
    // Use getEvaluationRuns API to check for PENDING/RUNNING evaluations
    this.truthManagementService.getEvaluationRuns(this.documentModel).subscribe({
      next: (response: any) => { 
        if (response.status === 'SUCCESS' && response.runs && response.runs.length > 0) {
          // Find any PENDING or RUNNING evaluations
          const activeRuns = response.runs.filter((run: any) => 
            run.status === 'PENDING' || run.status === 'RUNNING'
          );
          activeRuns.forEach((run: any) => {
            const activeEval: ActiveEvaluation = {
              evaluation_id: run.evaluation_id,
              job_id: run.evaluation_id,
              document_model: this.documentModel,
              file_count: run.total_files || 0,
              started_at: run.started_at,
              status: null
            };
            
            this.activeEvaluations.set(this.documentModel, activeEval);
            this.updateCurrentActiveEvaluation();
            
            // Start polling for this evaluation
            this.truthManagementService.startStatusPolling(run.evaluation_id, this.documentModel);
          });
          
          this.cdr.detectChanges();
        } else {
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Check if there is an active evaluation
   * NOTE: Template uses hasActiveEval property to avoid excessive calls
   */
  hasActiveEvaluation(): boolean {
    return this.hasActiveEval;
  }

  /**
   * Get active evaluation for current document model
   * NOTE: Template uses currentActiveEvaluation property to avoid excessive calls
   */
  getActiveEvaluation(): ActiveEvaluation | undefined {
    // Return the tab-appropriate active eval
    return this.currentActiveEvaluation;
  }
  /**
 * Check if there's an active FULL evaluation (throughout entire lifecycle)
 * This keeps the spinner visible even after first status update
 * @returns true if full evaluation is running (PENDING, RUNNING, or no status yet)
 */
hasActiveFullEvaluation(): boolean {
  return this.hasActiveFullEval;
}

/**
 * Check if there's an active TEST evaluation (throughout entire lifecycle)
 * This keeps the spinner visible even after first status update
 * @returns true if test evaluation is running (PENDING, RUNNING, or no status yet)
 */
hasActiveTestEvaluation(): boolean {
  return this.hasActiveTestEval;
}
  /**
   * Update the cached active evaluation property (called when status changes)
   */
  get currentActiveEvaluationStartTime(): Date | null {
    const s = this.currentActiveEvaluation?.started_at;
    if (!s) return null;
    return new Date(s.endsWith('Z') ? s : s + 'Z');
  }

  private updateCurrentActiveEvaluation(): void {
    const fullKey = `${this.documentModel}:all`;
    const testKey = `${this.documentModel}:part`;

    this.currentActiveFullEval = this.activeEvaluations.get(fullKey);
    this.currentActiveTestEval = this.activeEvaluations.get(testKey);

    // currentActiveEvaluation is the tab-appropriate one (used by card / pie-chart / progress)
    this.currentActiveEvaluation = this.runType === 'evaluation'
      ? this.currentActiveFullEval
      : this.currentActiveTestEval;

    this.hasActiveEval = !!(this.currentActiveFullEval || this.currentActiveTestEval);

    // Per-type active flags
    if (this.currentActiveFullEval) {
      const s = this.currentActiveFullEval.status?.status;
      this.hasActiveFullEval = s === 'RUNNING' || s === 'PENDING' || !s;
    } else {
      this.hasActiveFullEval = false;
    }
    if (this.currentActiveTestEval) {
      const s = this.currentActiveTestEval.status?.status;
      this.hasActiveTestEval = s === 'RUNNING' || s === 'PENDING' || !s;
    } else {
      this.hasActiveTestEval = false;
    }

    // Spinner flags: only block the SAME run type
    this.isStartingFullEval = this.startingEvaluation || this.hasActiveFullEval;
    this.isStartingTestEval = this.startingPartialEvaluation || this.hasActiveTestEval;

    // Recalculate progress / field stats from the currently-displayed run
    const activeStatus = this.currentActiveEvaluation?.status;
    if (activeStatus) {
      const total = activeStatus.total_files || 0;
      const completed = activeStatus.completed_files || 0;
      this.evaluationProgressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
      this.activeMatchedFieldsCount = this.getMatchedFields(activeStatus);
      this.activeUnmatchedFieldsCount = this.getUnmatchedFields(activeStatus);
      const totalFields = this.getTotalFields(activeStatus);
      this.activeMatchedFieldsPercent = this.getFieldPercent(this.activeMatchedFieldsCount, totalFields);
      this.activeUnmatchedFieldsPercent = this.getFieldPercent(this.activeUnmatchedFieldsCount, totalFields);
    } else {
      this.evaluationProgressPercent = 0;
      this.activeMatchedFieldsCount = 0;
      this.activeUnmatchedFieldsCount = 0;
      this.activeMatchedFieldsPercent = 0;
      this.activeUnmatchedFieldsPercent = 0;
    }
  }

  getEvaluationProgress(): number {
    const evaluation = this.activeEvaluations.get(this.documentModel);
    if (!evaluation?.status) return 0;
    
    const total = evaluation.status.total_files;
    const completed = evaluation.status.completed_files;
    
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }

  getElapsedTime(): string {
    return this.elapsedTime;
  }

  // ========== Pie Chart Helper Methods for Latest Run Summary ==========
  
  /**
   * Calculate stroke-dasharray for file status pie chart segments (Latest Run)
   */
  getFileStatusDashArray(type: 'completed' | 'running' | 'failed' | 'pending'): string {
    const total = this.latestRunStatus?.total_files || 0;
    if (total === 0) return '0 100';
    
    let count = 0;
    switch (type) {
      case 'completed':
        count = this.latestRunStatus?.completed_files || 0;
        break;
      case 'running':
        count = this.latestRunStatus?.running_files || 0;
        break;
      case 'failed':
        count = this.latestRunStatus?.failed_files || 0;
        break;
      case 'pending':
        count = this.latestRunStatus?.pending_files || 0;
        break;
    }
    
    const percentage = (count / total) * 100;
    return `${percentage} 100`;
  }
  
  /**
   * Calculate stroke-dashoffset for file status pie chart segments (Latest Run)
   */
  getFileStatusOffset(type: 'running' | 'failed' | 'pending'): number {
    const total = this.latestRunStatus?.total_files || 0;
    if (total === 0) return 0;
    
    const completed = ((this.latestRunStatus?.completed_files || 0) / total) * 100;
    const running = ((this.latestRunStatus?.running_files || 0) / total) * 100;
    const failed = ((this.latestRunStatus?.failed_files || 0) / total) * 100;
    
    switch (type) {
      case 'running':
        return completed;
      case 'failed':
        return completed + running;
      case 'pending':
        return completed + running + failed;
    }
  }
  
  // ========== Pie Chart Helper Methods for Active Evaluation ==========
  
  /**
   * Calculate stroke-dasharray for file status pie chart segments (Active Evaluation)
   */
  getActiveFileStatusDashArray(type: 'completed' | 'running' | 'failed' | 'pending'): string {
    const status = this.currentActiveEvaluation?.status;
    const total = status?.total_files || 0;
    if (total === 0) return '0 100';
    
    let count = 0;
    switch (type) {
      case 'completed':
        count = status?.completed_files || 0;
        break;
      case 'running':
        count = status?.running_files || 0;
        break;
      case 'failed':
        count = status?.failed_files || 0;
        break;
      case 'pending':
        count = status?.pending_files || 0;
        break;
    }
    
    const percentage = (count / total) * 100;
    return `${percentage} 100`;
  }
  
  /**
   * Calculate stroke-dashoffset for file status pie chart segments (Active Evaluation)
   */
  getActiveFileStatusOffset(type: 'running' | 'failed' | 'pending'): number {
    const status = this.currentActiveEvaluation?.status;
    const total = status?.total_files || 0;
    if (total === 0) return 0;
    
    const completed = ((status?.completed_files || 0) / total) * 100;
    const running = ((status?.running_files || 0) / total) * 100;
    const failed = ((status?.failed_files || 0) / total) * 100;
    
    switch (type) {
      case 'running':
        return completed;
      case 'failed':
        return completed + running;
      case 'pending':
        return completed + running + failed;
    }
  }
  


  copyEvaluationId(evaluationId: string): void {
    navigator.clipboard.writeText(evaluationId).then(() => {
      alert('Evaluation ID copied to clipboard!');
    }).catch(err => {
      alert('Failed to copy evaluation ID');
    });
  }

  /**
   * Navigate to validation results for a specific evaluation
   */
  viewValidationResults(run: ValidationRun): void {
    if (this.isEmbedded) {
      // When embedded, emit event to parent component
      this.evaluationSelected.emit(run.evaluationId);
    } else {
      // When standalone, navigate to run-validation-details
      this.router.navigate(['/run-validation', 1, run.evaluationId, this.documentModel]);
    }
  }

  /**
   * Run full validation for entire document model (evaluation run)
   */
  runFullValidation(): void {
    // Validate inputs
    if (!this.documentModel) {
      this.notificationService.showError('Document model unavailable. Please try again.');
      return;
    }
    
    // LAYER 2: Prevent double-clicks — block if same-type eval is already active
    if (this.startingEvaluation || this.hasActiveFullEval) {
      return;
    }
    
    // Capture the user's click time
    this.pendingStartTime = new Date().toISOString();
    
    // Show confirmation for full validation (no file selection)
    const message = `Are you sure you want to run validation for all the samples in this document model?`;
    const details = [
      { label: 'Document Model', value: this.documentModel },
      { label: 'Run Type', value: 'Full Document Model' }
    ];
    
    // Show confirmation modal
    this.modalService.showConfirmation(
      message,
      () => {
        // User confirmed - execute full validation (no file IDs = full run)
        this.executeValidation(undefined);
      },
      {
        title: 'Confirm Evaluation Run',
        confirmText: 'Yes, Run Evaluation',
        cancelText: 'Cancel',
        details: details
      }
    );
  }
  
  /**
   * Run test validation with file selection (partial run / test run)
   */
  runTestValidation(): void {
    
    // Validate inputs
    if (!this.documentModel) {
      this.notificationService.showError('Document model unavailable. Please try again.');
      return;
    }
    
    // Prevent double-clicks — block if same-type eval is already active
    if (this.startingPartialEvaluation || this.hasActiveTestEval) {
      return;
    }
    
    // Capture the user's click time
    this.pendingStartTime = new Date().toISOString();

    // Show file selection modal for test run (with testRunMode enabled)
   this.isTestRunMode = true; // Force partial mode, hide radio buttons
   this.showFileSelectionModal = true; // Show modal immediately (loader will display while loading)
   this.loadFilesForModal(); //  Load files next
}
  
  /**
   * @deprecated Legacy method - replaced by runFullValidation() and runTestValidation()
   * Kept for backward compatibility if called from unexpected places
   */
  runNewValidation(): void {
    // Default to full validation for backward compatibility
    this.runFullValidation();
  }
  
 /**
 * Load files for modal using shared service
 * Uses targeted API instead of full dashboard
 */
private loadFilesForModal(): void {
  this.isLoadingModalFiles = true;
  this.modalFiles = [];
  this.cdr.detectChanges();
  
  // Use shared service - intelligent caching and targeted fetch
  this.documentFilesService.getFiles(this.documentModel).subscribe({
    next: (files) => {
     files.forEach((file, index) => {
      });
      
      this.modalFiles = files;
      this.isLoadingModalFiles = false;
      this.cdr.detectChanges();
    },
    error: (error) => {
      this.modalFiles = [];
      this.isLoadingModalFiles = false;
      this.cdr.detectChanges();
    }
  });
}
/**
 * Pre-load files for modal in background (for instant display when user clicks Run Test)
 */
private preloadFilesForModal(): void {
  if (!this.documentModel) {
    return;
  }
  
  // Silent background load - don't show loading indicators
  this.documentFilesService.getFiles(this.documentModel).subscribe({
    next: (files) => {// Files are now cached in the service
     },
    error: (error) => {
      // Silent fail - will retry when user clicks "Run Test"
    }
  });
}

getFilesForDocumentModel(): DashboardEvalFile[] {
  return this.modalFiles;
}
  
  /**
   * Handle file selection modal confirmation
   * NOTE: This is ONLY used for test runs (partial validation with selected files)
   */
  handleFileSelectionConfirm(fileIds: string[]): void {
    // Test run requires at least one file to be selected
    if (fileIds.length === 0) {
      this.notificationService.showError('Test run requires at least one file to be selected.');
      return;
    }
    
    // Close file selection modal first
    this.showFileSelectionModal = false;
    
    // Show confirmation for test run (partial validation)
    const message = `Are you sure you want to run validation for ${fileIds.length} selected sample(s)?`;
    const details = [
      { label: 'Document Model', value: this.documentModel },
      { label: 'Run Type', value: 'Test Run (Partial)' },
      { label: 'Selected Samples', value: `${fileIds.length} sample(s)` }
    ];
    
    // Show confirmation modal
    this.modalService.showConfirmation(
      message,
      () => {
        // User confirmed - execute test validation with selected samples
        this.executeValidation(fileIds);
        this.isTestRunMode = false;
      },
      {
        title: 'Confirm Test Run',
        confirmText: 'Yes, Run Test',
        cancelText: 'Cancel',
        details: details
      }
    );
  }
  
  /**
   * Handle file selection modal cancellation
   */
  handleFileSelectionCancel(): void {
    this.showFileSelectionModal = false;
    this.isTestRunMode = false; // Reset test run mode
  }
  
  /**
   * Toggle between Evaluation Runs and Test Runs view
   */
  toggleRunType(type: 'evaluation' | 'test'): void {
    this.runType = type;

    // Update editableEvaluationId based on new tab
    if (type === 'evaluation' && this.latestEvalRun) {
      this.editableEvaluationId = this.latestEvalRun.evaluationId || '';
    } else if (type === 'test' && this.latestTestRun) {
      this.editableEvaluationId = this.latestTestRun.evaluationId || '';
    }

    // Refresh tab-aware cached properties (progress card, timer, status badge)
    this.updateCurrentActiveEvaluation();

    // Lazily fetch status for the newly active tab only if not yet loaded.
    // This avoids a redundant eval/status call on initial load for the inactive tab.
    if (type === 'test' && !this.latestTestRunStatus && this.latestTestRun?.evaluationId) {
      this.fetchLatestRunStatus();
    } else if (type === 'evaluation' && !this.latestEvalRunStatus && this.latestEvalRun?.evaluationId) {
      this.fetchLatestRunStatus();
    }

    this.cdr.detectChanges();
  }
  
  /**
   * Get displayed runs based on current run type selection
   */
  get displayedRuns(): ValidationRun[] {
    return this.runType === 'evaluation' ? this.evaluationRuns : this.testRuns;
  }
  
  /**
   * Execute the validation after file selection
   * @param fileIds Optional array of file UUIDs for partial validation (undefined = full run)
   */
  private executeValidation(fileIds?: string[]): void {
    const isPartialRun = fileIds && fileIds.length > 0;
    const runType = isPartialRun ? 'part' : 'all';
    if (isPartialRun) {
    }
    
    // ============================================
    // Pre-Flight Check: Now handled inside runEvaluationSafe() via API call
    // ============================================
    
    // LAYER 4: Guard — prevent duplicate execution if same-type eval is already active
    if (isPartialRun) {
      if (this.startingPartialEvaluation || this.hasActiveTestEval) {
        return;
      }
    } else {
      if (this.startingEvaluation || this.hasActiveFullEval) {
        return;
      }
    }
    
    // SET LOADING STATE: Spinner shows until evaluation completes
    if (isPartialRun) {
      this.startingPartialEvaluation = true;
    } else {
      this.startingEvaluation = true;
    }

    // Auto-switch tab to match the run type being started
    this.runType = isPartialRun ? 'test' : 'evaluation';

    this.updateCurrentActiveEvaluation(); // Update cached active evaluation before API call
    this.cdr.detectChanges();
    
    // Auth check - reset loading state if auth fails
    if (!this.isAuthenticated) {
      if (isPartialRun) {
        this.startingPartialEvaluation = false;
      } else {
        this.startingEvaluation = false;
      }
      this.cdr.detectChanges();
      this.notificationService.showError('Please login to run validation.');
      return;
    }
    // User is actively starting a run — any completion from this point on is a real new
    // completion, not an artifact of the initial page load.
    this.isInitialLoad = false;

    // Call API using SAFE wrapper with request deduplication
    this.truthManagementService.runEvaluationSafe(this.documentModel, fileIds).subscribe({
      next: (response) => {
        // Check if API returned FAILED status (e.g., no evaluation files found)
        if (response.status === 'FAILED') {
          if (isPartialRun) {
            this.startingPartialEvaluation = false;
          } else {
            this.startingEvaluation = false;
          }
          this.updateCurrentActiveEvaluation();
          this.cdr.detectChanges();
          
          const runLabelErr = isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start';
          const failReason = response.message ? 'No files found for evaluation' : 'Technical error';
          this.notificationService.showError(
            runLabelErr,
            [
              { label: 'Document model', value: this.documentModel, originalValue: this.documentModel },
              { label: 'Reason', value: failReason, originalValue: failReason }
            ]
          );
          return;
        }

        if (response.skipped_files && response.skipped_files.length > 0) {
          response.skipped_files.forEach((file: string) => {
          });
        }
        
        // CRITICAL: Check if ANY files will be processed
        if (response.file_count === 0) {
          if (isPartialRun) {
            this.startingPartialEvaluation = false;
          } else {
            this.startingEvaluation = false;
          }
          this.updateCurrentActiveEvaluation();
          this.cdr.detectChanges();

          // Show clean structured error notification
          const runLabelErr = isPartialRun ? 'Run Test Failed to start' : 'Evaluation Run Failed to start';
          this.notificationService.showError(
            runLabelErr,
            [
              { label: 'Document model', value: this.documentModel, originalValue: this.documentModel },
              { label: 'Reason', value: 'no ground truth' }
            ]
          );
          return;
        }
        
        // Show ONLY ONE notification based on whether files were skipped
        if (response.skipped_count > 0) {
          // Has skipped files → show detailed notification with document model and skipped count
          const displayDocName = this.documentModel
            ? this.documentModel.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            : 'Document';
          const runTypeMessage = isPartialRun ? 'Test run started successfully' : 'Evaluation run started successfully';
          this.notificationService.showSuccess(
            runTypeMessage,
            [
              { label: 'Document model', value: this.documentModel, originalValue: this.documentModel },
              { label: 'Skipped files', value: response.skipped_count }
            ]
          );
        } else {
          // No skipped files → show simple notification (no document model details)
          this.notificationService.showRunInitiated(this.documentModel, isPartialRun ? 'part' : 'all');
        }
        
        const evaluationId = response.evaluation_id || response.job_id || response.id;
        if (!evaluationId || evaluationId === 'undefined') {
          if (isPartialRun) {
            this.startingPartialEvaluation = false;
          } else {
            this.startingEvaluation = false;
          }
          this.updateCurrentActiveEvaluation(); // FIX: Update cached button states
          this.cdr.detectChanges();
          const noIdReason = response.message ? 'No files found for evaluation' : 'Technical error';
          this.notificationService.showError(isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start', [
            { label: 'Document model', value: this.documentModel, originalValue: this.documentModel },
            { label: 'Reason', value: noIdReason, originalValue: noIdReason }
          ]);
          return;
        }
        // Create active evaluation object with response data
        // started_at is left blank — the service will anchor the timer to NOW
        // when the first RUNNING poll delivers status.started_at.
        const activeEval: ActiveEvaluation = {
          evaluation_id: evaluationId,
          job_id: response.job_id || evaluationId,
          document_model: this.documentModel,
          file_count: response.file_count || 0,
          started_at: '',  // blank until first RUNNING poll
          status: null,
          run_type: isPartialRun ? 'part' : 'all'  // Track run type
        };
        // Validate the activeEval object before saving
        if (!activeEval.evaluation_id || activeEval.evaluation_id === 'undefined') {
          if (isPartialRun) {
            this.startingPartialEvaluation = false;
          } else {
            this.startingEvaluation = false;
          }
          this.updateCurrentActiveEvaluation(); // FIX: Update cached button states
          this.cdr.detectChanges();
          this.notificationService.showError('Received invalid evaluation ID from server');
          return;
        }
        
        // Store active evaluation in service (ensures navigation persistence)
        this.truthManagementService.setActiveEvaluation(this.documentModel, activeEval);
        // Update local Map immediately (compound key) so cached lookups stay accurate
        this.activeEvaluations.set(`${this.documentModel}:${isPartialRun ? 'part' : 'all'}`, activeEval);
        
        // Start status polling in service (centralized polling)
        this.truthManagementService.startStatusPolling(evaluationId, this.documentModel);
        
        // Emit initial PENDING status to parent (if embedded)
        if (this.isEmbedded) {
          const initialStatus: EvaluationStatus = {
            evaluation_id: evaluationId,
            document_model: this.documentModel,
            status: 'PENDING',
            total_files: response.file_count || 0,
            completed_files: 0,
            failed_files: 0,
            running_files: 0,
            pending_files: response.file_count || 0,
            started_at: '',
            completed_at: null,
            files: [],
            results: []
          };
          this.evaluationStatusChanged.emit(initialStatus);
        }

        // Clear the pending start time now that we've committed the run
        this.pendingStartTime = null;

        // Service is already handling polling via line 1723
        // No need for duplicate component-level polling
        
        // Trigger UI update immediately to show active evaluation card
        this.cdr.detectChanges();
        
        // DON'T clear loading flags here - they'll be cleared by service when polling returns COMPLETED/FAILED
      },
      error: (error) => {
        // ============================================
        // SPECIAL CASE: Pre-Flight Check Failed (Active Run Exists)
        // Solution: Adopt the active run and start polling it
        // This ensures Browser B shows spinner and completion popup
        // ============================================
        if (error.isPreFlightFailure && error.activeRun) {
          // Create ActiveEvaluation from the existing run
          const activeEval: ActiveEvaluation = {
            evaluation_id: error.activeRun.evaluation_id,
            job_id: error.activeRun.evaluation_id,
            document_model: this.documentModel,
            file_count: error.activeRun.total_files || 0,
            started_at: error.activeRun.started_at,
            status: null, // Will be fetched by polling
            run_type: error.activeRun.files_run === error.activeRun.total_files ? 'all' : 'part'
          };
          
          // Register with service (enables cross-browser awareness)
          this.truthManagementService.setActiveEvaluation(this.documentModel, activeEval);
          this.activeEvaluations.set(`${this.documentModel}:${activeEval.run_type}`, activeEval);
          
          // Start polling for updates
          this.truthManagementService.startStatusPolling(error.activeRun.evaluation_id, this.documentModel);
          
          // Update cached properties for UI
          this.updateCurrentActiveEvaluation();
          
          // Keep spinner visible (don't clear startingEvaluation/startingPartialEvaluation)
          // Show structured toaster matching the standard format
          const preFlightReason = (error.error?.message || error.error?.detail) ? 'No files found for evaluation' : 'Technical error';
          this.notificationService.showError(isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start', [
            { label: 'Document model', value: this.documentModel, originalValue: this.documentModel },
            { label: 'Reason', value: preFlightReason, originalValue: preFlightReason }
          ]);
          
          this.cdr.detectChanges();
          return; // Exit early - don't treat as error
        }
        
        // ============================================
        // NORMAL ERROR HANDLING (API failures, auth errors, etc.)
        // ============================================
        // Reset loading state on error
        if (isPartialRun) {
          this.startingPartialEvaluation = false;
        } else {
          this.startingEvaluation = false;
        }
        this.updateCurrentActiveEvaluation(); // FIX: Update cached button states
        this.cdr.detectChanges();
        
        const runLabelErr = isPartialRun ? 'Run test failed to start' : 'Evaluation run failed to start';
        const errorReason = (error.error?.message || error.error?.detail) ? 'No files found for evaluation' : 'Technical error';
        this.notificationService.showError(runLabelErr, [
          { label: 'Document model', value: this.documentModel, originalValue: this.documentModel },
          { label: 'Reason', value: errorReason, originalValue: errorReason }
        ]);
      }
    });
  }

  pauseValidation(): void {
    const activeEval = this.currentActiveEvaluation;
    if (!activeEval) {
      return;
    }
    
    // Stop the polling
    const interval = this.pollingIntervals.get(this.documentModel);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(this.documentModel);
    }
    
    // Remove from active evaluations (service state)
    this.truthManagementService.clearActiveEvaluation(this.documentModel);
    
    // Trigger UI update
    this.cdr.detectChanges();
  }

  stopStatusPolling(evaluationId: string): void {
    const interval = this.pollingIntervals.get(evaluationId);
    if (interval) {
      clearInterval(interval);
      this.pollingIntervals.delete(evaluationId);
      }
    
    // Clear error counter
    this.pollingErrorCounts.delete(evaluationId);
    
    // Timer cleanup is now handled by service
    this.evalElapsedTime = '';
    this.testElapsedTime = '';
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
    });
  }

  /**
   * Format duration in milliseconds to human-readable string
   */
  formatDuration(durationMs: number): string {
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    const secs = seconds % 60;
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    } else if (minutes > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  // Debug method to clear active evaluations - can be called from browser console
  clearEvaluationStorage(): void {
    this.activeEvaluations.clear();
    this.updateCurrentActiveEvaluation();
    this.validationRuns = [];
    
    // Stop all polling
    this.pollingIntervals.forEach(interval => clearInterval(interval));
    this.pollingIntervals.clear();
    alert('Active evaluation storage cleared. Please refresh the page.');
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  onEvalInfoClick(event: MouseEvent, run: ValidationRun): void {
    event.stopPropagation();
    const triggerElement = event.currentTarget as HTMLElement | null;
    if (!triggerElement) { return; }

    if (this.isEvalDetailsPopupOpen && this.activeEvalDetailsRun === run) {
      this.closeEvalDetailsPopup();
      return;
    }

    this.activeEvalDetailsRun = run;
    this.isEvalDetailsPopupOpen = true;

    const rect = triggerElement.getBoundingClientRect();
    this.positionEvalDetailsPopup(rect, 256);
    this.cdr.detectChanges();

    setTimeout(() => {
      if (!this.isEvalDetailsPopupOpen || this.activeEvalDetailsRun !== run) { return; }
      const measuredHeight = this.evalDetailsPopup?.nativeElement.getBoundingClientRect().height ?? 256;
      this.positionEvalDetailsPopup(rect, measuredHeight);
      this.cdr.detectChanges();
    });
  }

  closeEvalDetailsPopup(): void {
    this.isEvalDetailsPopupOpen = false;
    this.activeEvalDetailsRun = null;
    this.evalDetailsPopupMaxHeight = 'none';
  }

  private positionEvalDetailsPopup(triggerRect: DOMRect, popupHeight: number): void {
    const popupWidth = 288;
    const viewportPadding = 8;
    const popupGap = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = triggerRect.left + (triggerRect.width / 2) - (popupWidth / 2);
    left = Math.max(viewportPadding, Math.min(left, viewportWidth - popupWidth - viewportPadding));

    const availableBelow = Math.max(0, viewportHeight - viewportPadding - (triggerRect.bottom + popupGap));
    const availableAbove = Math.max(0, triggerRect.top - popupGap - viewportPadding);
    const placeBelow = availableBelow >= availableAbove;
    const maxHeight = placeBelow ? availableBelow : availableAbove;
    this.evalDetailsPopupMaxHeight = `${Math.floor(maxHeight)}px`;

    let top = triggerRect.bottom + popupGap;
    if (!placeBelow) {
      const visibleHeight = Math.min(popupHeight, maxHeight);
      top = triggerRect.top - popupGap - visibleHeight;
    }

    this.evalDetailsPopupPosition = { left, top };
  }
}
